import { axiosClient } from "./http-client.service";
import { Conversation } from "../models/chat.model";

// API Configuration
export interface WhatsAppMessage {
  id: string;
  from: {
    id: string;
    name?: string;
  };
  to?: {
    id: string;
  };
  type: "text" | "image" | "audio" | "video" | "document";
  text?: {
    body: string;
  };
  timestamp: string;
  status?: "sent" | "delivered" | "read" | "failed";
}

export interface SendMessagePayload {
  messaging_product: "whatsapp";
  recipient_type: "individual";
  to: string;
  type: "text";
  text: {
    body: string;
  };
}

export interface PageResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
}

export type ConversationStatus = "OPEN" | "PENDING" | "RESOLVED";
export type ConversationPriority = "NORMAL" | "HIGH" | "URGENT";

export interface ConversationSummary extends Conversation {
  unreadCount: number;
  status: ConversationStatus;
  priority: ConversationPriority;
  assignedTo?: string;
  labels: string[];
  notes?: string;
  markedUnread: boolean;
}

export interface ConversationMetadataUpdate {
  status?: ConversationStatus;
  priority?: ConversationPriority;
  assignedTo?: string;
  labels?: string[];
  notes?: string;
  markedUnread?: boolean;
}

export const sendMessage = async (
  settingsId: string,
  toPhoneNumber: string,
  messageText: string
): Promise<any> => {
  try {
    const url = `/chat/message/send`;
    const response = await axiosClient.post(
      url,
      {
        messaging_product: "whatsapp",
        to: toPhoneNumber.replace(/\s+/g, ""), // strip spaces
        type: "text",
        text: {
          body: messageText,
        },
      },
      {
        params: {
          settingsId,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error sending message:", error);
    throw error;
  }
};
/**
 * Get all conversations for the business account
 */
export const getConversations = async (
  businessPhoneNumber: string,
  options: {
    page?: number;
    size?: number;
    search?: string;
    status?: "ALL" | ConversationStatus;
    unreadOnly?: boolean;
  } = {},
): Promise<PageResponse<ConversationSummary>> => {
  try {
    // if (!ACCESS_TOKEN || !PHONE_NUMBER_ID) {
    //   console.warn("WhatsApp API credentials not configured. Using mock data.");
    //   return getMockConversations();
    // }

    const url = `/chat/${businessPhoneNumber}/conversations`;
    const response = await axiosClient.get(url, {
      params: {
        page: options.page ?? 0,
        size: options.size ?? 30,
        search: options.search || undefined,
        status: options.status ?? "ALL",
        unreadOnly: options.unreadOnly ?? false,
      },
    });

    if (response.data) {
      return response.data as PageResponse<ConversationSummary>;
    }
    return {
      content: [],
      page: 0,
      size: options.size ?? 30,
      totalElements: 0,
      totalPages: 0,
      hasNext: false,
    };
  } catch (error) {
    console.error("Error fetching conversations:", error);
    throw error;
  }
};

/**
 * Get all messages for a specific conversation
 * @param customerPhoneNumber - The customer's phone number
 */
export const getConversationMessages = async (
  businessPhoneNumber: string,
  customerPhoneNumber: string,
  page = 0,
  size = 50,
): Promise<PageResponse<Conversation>> => {
  try {
    if (!businessPhoneNumber) {
      console.warn("WhatsApp businessPhoneNumber not configured.");
      return {
        content: [],
        page,
        size,
        totalElements: 0,
        totalPages: 0,
        hasNext: false,
      };
    }

    const url = `/chat/conversation/${businessPhoneNumber}`;
    const response = await axiosClient.get(url, {
      params: {
        customerPhoneNumber,
        page,
        size,
      },
    });

    if (response.data) {
      return response.data as PageResponse<Conversation>;
    }
    return {
      content: [],
      page,
      size,
      totalElements: 0,
      totalPages: 0,
      hasNext: false,
    };
  } catch (error) {
    console.error("Error fetching conversation messages:", error);
    throw error;
  }
};

export const updateConversationMetadata = async (
  businessPhoneNumber: string,
  customerPhoneNumber: string,
  update: ConversationMetadataUpdate,
): Promise<void> => {
  await axiosClient.put(
    `/chat/conversation/${encodeURIComponent(businessPhoneNumber)}/metadata/${encodeURIComponent(customerPhoneNumber)}`,
    update,
  );
};
